// src/components/empleado/MisAusencias.jsx (modificado según tus archivos)
// Reglas:
//  - AUSENCIA/CANCELACION ⇒ muestra fechas originales (a.fecha_inicio/fin ...)
//  - CAMBIO ⇒ muestra SIEMPRE lo solicitado en a.payload.{fecha_inicio,fecha_fin,hora_inicio,hora_fin,parcial} si existe
//  - Oculta SOLO solicitudes de CAMBIO aprobadas (la ausencia ya refleja el cambio)
//  - Filtros/ordenación/CSV usan las fechas que se muestran

import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { ausenciasService } from "../../services/ausencias";
import { fmtFechaCorta, fmtHoraCorta } from "../../utils/fecha";
import Paginacion from "../shared/Paginacion";

const TODOS = "TODOS";

const ESTADO_CLASE = {
  APROBADA: "bg-emerald-100 text-emerald-700 ring-1 ring-emerald-300/50",
  RECHAZADA: "bg-rose-100 text-rose-700 ring-1 ring-rose-300/50",
  PENDIENTE: "bg-amber-100 text-amber-700 ring-1 ring-amber-300/50",
};

const KIND_BADGE = {
  AUSENCIA: "bg-slate-100 text-slate-700 ring-1 ring-slate-300/60",
  CAMBIO: "bg-indigo-100 text-indigo-700 ring-1 ring-indigo-300/60",
  CANCELACION: "bg-rose-100 text-rose-700 ring-1 ring-rose-300/60",
};

const TIPO_BADGE = {
  VACACIONES: "bg-gradient-to-r from-indigo-500/15 to-indigo-400/15 text-indigo-800 ring-1 ring-indigo-300/40",
  BAJA: "bg-gradient-to-r from-rose-500/15 to-rose-400/15 text-rose-800 ring-1 ring-rose-300/40",
  CITA_MEDICA: "bg-gradient-to-r from-fuchsia-500/15 to-fuchsia-400/15 text-fuchsia-800 ring-1 ring-fuchsia-300/40",
  ASUNTOS_PROPIOS: "bg-gradient-to-r from-sky-500/15 to-sky-400/15 text-sky-800 ring-1 ring-sky-300/40",
  OTRA: "bg-gradient-to-r from-slate-500/15 to-slate-400/15 text-slate-800 ring-1 ring-slate-300/40",
};

const COLS = [
  { key: "tipo", label: "Tipo" },
  { key: "fechas", label: "Fechas" },
  { key: "parcial", label: "Parcial" },
  { key: "retribuida", label: "Retribuida" },
  { key: "estado", label: "Estado" },
  { key: "motivo", label: "Motivo" },
  { key: "acciones", label: "Acciones" },
];
// === Etiquetas legibles para Tipo/Subtipo ===
const LABEL_MAP = {
  "VACACIONES": "Vacaciones",
  "PERMISO": "Permiso",
  "CITA_MEDICA": "Cita médica",
  "ACCIDENTE_FAMILIAR": "Accidente familiar",
  "ASUNTOS_PROPIOS": "Asuntos propios",
  "BAJA": "Baja",
  "BAJA_MEDICA": "Baja médica",
  "ENFERMEDAD": "Enfermedad",
  "OTRA": "Otra"
};

const toTitle = (s) => String(s || "")
  .toLowerCase()
  .split(/\s+/)
  .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : ""))
  .join(" ");

function prettyLabel(raw) {
  if (!raw) return "";
  const key = String(raw).trim().toUpperCase().replace(/\s+/g, "_");
  if (LABEL_MAP[key]) return LABEL_MAP[key];
  return toTitle(String(raw).replaceAll("_", " "));
}

function combineTipoSubtipo(tipo, subtipo) {
  const t = prettyLabel(tipo);
  const s = prettyLabel(subtipo);
  if (!s || t.toLowerCase() === s.toLowerCase()) return t;
  return `${t} · ${s}`;
}


const safe = (v) => (v ?? "").toString();
const toISODate = (d) => {
  if (!d) return "";
  if (d instanceof Date) return d.toISOString().slice(0, 10);
  const s = String(d);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const nd = new Date(s);
  return isNaN(nd) ? "" : nd.toISOString().slice(0, 10);
};
const hhmm = (h) => {
  const s = safe(h);
  if (/^\d{2}:\d{2}/.test(s)) return s.slice(0, 5);
  const d = new Date(s);
  if (!isNaN(d)) {
    const H = String(d.getUTCHours()).padStart(2, "0");
    const M = String(d.getUTCMinutes()).padStart(2, "0");
    return `${H}:${M}`;
  }
  return "";
};

// === Qué fechas/horas se muestran ===
function getDisplay(a) {
  const kind = a?.kind || (a?._is_request ? (a?._request_kind === "change" ? "CAMBIO" : "CANCELACION") : "AUSENCIA");

  if (kind === "CAMBIO") {
    // Lo solicitado viene en el payload (según tu servicio)  fileciteturn7file2
    const p = a?.payload && typeof a.payload === "string" ? safe(a.payload) : a?.payload || {};
    let payload = p;
    try { if (typeof p === "string") payload = JSON.parse(p); } catch { payload = {}; }

    const fecha_inicio = payload.fecha_inicio ?? payload.nueva_fecha_inicio ?? payload.req_fecha_inicio ?? a.fecha_inicio;
    const fecha_fin = payload.fecha_fin ?? payload.nueva_fecha_fin ?? payload.req_fecha_fin ?? a.fecha_fin;
    const parcial = (payload.parcial ?? payload.nueva_parcial ?? payload.req_parcial ?? a.parcial) || false;
    const hora_inicio = payload.hora_inicio ?? payload.nueva_hora_inicio ?? payload.req_hora_inicio ?? a.hora_inicio ?? null;
    const hora_fin = payload.hora_fin ?? payload.nueva_hora_fin ?? payload.req_hora_fin ?? a.hora_fin ?? null;

    return { fecha_inicio, fecha_fin, parcial, hora_inicio, hora_fin };
  }

  // AUSENCIA o CANCELACION ⇒ originales
  return {
    fecha_inicio: a.fecha_inicio,
    fecha_fin: a.fecha_fin,
    parcial: !!a.parcial,
    hora_inicio: a.hora_inicio || null,
    hora_fin: a.hora_fin || null,
  };
}


// === Formateo visual de la columna "Fechas" ===
const sameDay = (aISO, bISO) => {
  if (!aISO || !bISO) return false;
  return String(aISO).slice(0,10) == String(bISO).slice(0,10);
};

function renderFechas(d) {
  const fi = toISODate(d.fecha_inicio);
  const ff = toISODate(d.fecha_fin);
  const hi = hhmm(d.hora_inicio);
  const hf = hhmm(d.hora_fin);
  const parcial = !!d.parcial;

  
  const hasHours = !!(hi || hf);
if (sameDay(fi, ff) && !parcial && !hasHours) {
    return (
      <div className="flex flex-wrap items-center gap-1 sm:gap-2">
        <span className="font-medium">{fmtFechaCorta(fi)}</span>
      </div>
    );
  }

  if (sameDay(fi, ff) && (parcial || hasHours)) {
    return (
      <div className="flex flex-wrap items-center gap-1 sm:gap-2">
        <span className="font-medium">{fmtFechaCorta(fi)}</span>
        {(hi || hf) && <span className="text-slate-600">({hi || ""}{hi && hf ? " - " : ""}{hf || ""})</span>}
      </div>
    );
  }

  if (!parcial) {
    return (
      <div className="flex flex-wrap items-center gap-1 sm:gap-2">
        <span className="font-medium">{fmtFechaCorta(fi)}</span>
        <span className="text-slate-400">→</span>
        <span className="font-medium">{fmtFechaCorta(ff)}</span>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-1 sm:gap-2">
      <span className="font-medium">{fmtFechaCorta(fi)}</span>
      {hi && <span className="text-slate-600">{hi}</span>}
      <span className="text-slate-400">→</span>
      <span className="font-medium">{fmtFechaCorta(ff)}</span>
      {hf && <span className="text-slate-600">{hf}</span>}
    </div>
  );
}
function toCSV(rows) {
  const header = ["Tipo","Subtipo","Fecha inicio","Hora inicio","Fecha fin","Hora fin","Parcial","Retribuida","Estado","Motivo"];
  const lines = rows.map((a) => {
    const d = getDisplay(a);
    return [
      safe(a.tipo),
      safe(a.subtipo),
      toISODate(d.fecha_inicio),
      hhmm(d.hora_inicio),
      toISODate(d.fecha_fin),
      hhmm(d.hora_fin),
      d.parcial ? "Sí" : "No",
      a.retribuida ? "Sí" : "No",
      safe(a.estado),
      safe(a.motivo).replaceAll("\\n", " "),
    ];
  });
  const escape = (s) => (/[\",\\n]/.test(s) ? `\"${String(s).replaceAll('\"','\"\"')}\"` : s);
  const csv = [header, ...lines].map((row) => row.map((c) => escape(String(c ?? ""))).join(",")).join("\\n");
  return "\\uFEFF" + csv;
}

function useHotkeys({ searchRef, onRefresh, onExport, onCopy }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "/" && searchRef?.current) { e.preventDefault(); searchRef.current.focus(); }
      if (e.key.toLowerCase() === "r" && onRefresh) { e.preventDefault(); onRefresh(); }
      if (e.key.toLowerCase() === "e" && onExport) { e.preventDefault(); onExport(); }
      if (e.key.toLowerCase() === "c" && onCopy) { e.preventDefault(); onCopy(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [searchRef, onRefresh, onExport, onCopy]);
}

const isFuture = (a) => {
  const start = a?.fecha_inicio ? new Date(a.fecha_inicio) : null;
  return !!start && start.getTime() > Date.now();
};
const canOperate = (a) => (a?.kind ? a.kind === "AUSENCIA" : true) && isFuture(a) && (a.estado === "PENDIENTE" || a.estado === "APROBADA");
const kindBadgeClass = (kind) => KIND_BADGE[kind] || KIND_BADGE.AUSENCIA;

export default function MisAusencias({ token, data = [], cargando = false, onRefrescar }) {
  const searchRef = useRef(null);

  const [estado, setEstado] = useState(TODOS);
  const [tipo, setTipo] = useState(TODOS);
  const [q, setQ] = useState("");
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [sort, setSort] = useState({ key: "fechas", dir: "desc" });
  const [page, setPage] = useState(1);
  const perPage = 10;

  useHotkeys({ searchRef, onRefresh: onRefrescar, onExport: handleExport, onCopy: handleCopy });

  // --- Modales edición/cancelación ---
  const [editOpen, setEditOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({ fecha_inicio: "", fecha_fin: "", parcial: false, hora_inicio: "", hora_fin: "" });
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelMotivo, setCancelMotivo] = useState("");

  const tiposUnicos = useMemo(() => [TODOS, ...Array.from(new Set((data || []).map((d) => d?.tipo).filter(Boolean)))] ,[data]);
  const estadosUnicos = useMemo(() => [TODOS, ...Array.from(new Set((data || []).map((d) => d?.estado).filter(Boolean)))],[data]);

  // Filtrar (oculta SOLO CAMBIO aprobada)
  const filtradas = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (data || [])
      .filter((a) => {
        if ((a?.kind === "CAMBIO" || a?._request_kind === "change") && a.estado === "APROBADA") return false;
        if (estado !== TODOS && a.estado !== estado) return false;
        if (tipo !== TODOS && a.tipo !== tipo) return false;
        const d = getDisplay(a);
        const start = toISODate(d.fecha_inicio);
        const end = toISODate(d.fecha_fin);
        if (desde && end && end < desde) return false;
        if (hasta && start && start > hasta) return false;
        if (needle) {
          const hay = [a.tipo, a.subtipo, a.motivo, start, end, hhmm(d.hora_inicio), hhmm(d.hora_fin)]
            .filter(Boolean).join(" ").toLowerCase().includes(needle);
          if (!hay) return false;
        }
        return true;
      });
  }, [data, estado, tipo, q, desde, hasta]);

  // Ordenar por las fechas que se muestran
  const ordenadas = useMemo(() => {
    const arr = [...filtradas];
    const dir = sort.dir === "asc" ? 1 : -1;
    arr.sort((a, b) => {
      const key = sort.key;
      if (key === "tipo") {
        const at = `${safe(a.tipo)} ${safe(a.subtipo)}`.toLowerCase();
        const bt = `${safe(b.tipo)} ${safe(b.subtipo)}`.toLowerCase();
        return at > bt ? dir : at < bt ? -dir : 0;
      }
      if (key === "fechas") {
        const da = getDisplay(a), db = getDisplay(b);
        const aKey = `${toISODate(da.fecha_inicio)} ${hhmm(da.hora_inicio) || "00:00"}`;
        const bKey = `${toISODate(db.fecha_inicio)} ${hhmm(db.hora_inicio) || "00:00"}`;
        return aKey > bKey ? dir : aKey < bKey ? -dir : 0;
      }
      if (key === "parcial" || key === "retribuida") {
        const av = !!a[key]; const bv = !!b[key];
        return av === bv ? 0 : av ? dir : -dir;
      }
      if (key === "estado") {
        const order = { APROBADA: 3, PENDIENTE: 2, RECHAZADA: 1 };
        return ((order[a.estado] || 0) - (order[b.estado] || 0)) * dir;
      }
      if (key === "motivo") {
        const am = safe(a.motivo).toLowerCase(); const bm = safe(b.motivo).toLowerCase();
        return am > bm ? dir : am < bm ? -dir : 0;
      }
      return 0;
    });
    return arr;
  }, [filtradas, sort]);

  const total = ordenadas.length;
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const paginated = useMemo(() => {
    const start = (page - 1) * perPage;
    return ordenadas.slice(start, start + perPage);
  }, [ordenadas, page]);

  useEffect(() => { setPage(1); }, [estado, tipo, q, desde, hasta]);

  function toggleSort(key) {
    setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));
  }
  const ariaSortFor = (key) => (sort.key === key ? (sort.dir === "asc" ? "ascending" : "descending") : "none");

  function handleExport() {
    const csv = toCSV(ordenadas);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const fecha = new Date().toISOString().slice(0, 10);
    a.href = url; a.download = `mis-ausencias_${fecha}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  }
  async function handleCopy() {
    const csv = toCSV(ordenadas);
    try { await navigator.clipboard.writeText(csv); } catch {}
  }

  const openEdit = (a) => {
    setEditTarget(a);
    const d = getDisplay(a);
    setEditForm({
      fecha_inicio: toISODate(d.fecha_inicio) || "",
      fecha_fin: toISODate(d.fecha_fin) || "",
      parcial: !!d.parcial,
      hora_inicio: hhmm(d.hora_inicio) || "",
      hora_fin: hhmm(d.hora_fin) || "",
    });
    setEditOpen(true);
  };
  const openCancel = (a) => {
    setCancelTarget(a);
    setCancelMotivo("");
    setCancelOpen(true);
  };

  const doSolicitarCambio = async () => {
    if (!token) return toast.error("Sesión no válida.");
    try {
      const payload = {
        tipo: editTarget?.tipo ?? "VACACIONES",
        fecha_inicio: editForm.fecha_inicio || null,
        fecha_fin: editForm.fecha_fin || null,
        parcial: !!editForm.parcial,
        hora_inicio: editForm.parcial ? (editForm.hora_inicio || null) : null,
        hora_fin: editForm.parcial ? (editForm.hora_fin || null) : null,
        retribuida: !!editTarget?.retribuida,
      };
      const motivo = "";
      await ausenciasService.solicitarCambio(token, editTarget.id, payload, motivo);
      toast.success("Solicitud de cambio enviada");
      setEditOpen(false);
      setEditTarget(null);
      onRefrescar && onRefrescar();
    } catch (e) {
      toast.error(e?.message || "Error al solicitar cambio");
    }
  };

  const doSolicitarCancelacion = async () => {
    if (!token) return toast.error("Sesión no válida.");
    if (!cancelMotivo.trim()) return toast.info("Indica un motivo de cancelación.");
    try {
      await ausenciasService.solicitarCancelacion(token, cancelTarget.id, cancelMotivo.trim());
      toast.success("Solicitud de cancelación enviada");
      setCancelOpen(false);
      setCancelTarget(null);
      setCancelMotivo("");
      onRefrescar && onRefrescar();
    } catch (e) {
      toast.error(e?.message || "Error al solicitar cancelación");
    }
  };

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="rounded-2xl bg-white/55 backdrop-blur-xl border border-white/50 shadow-md p-3">
        <div className="flex flex-col xl:flex-row xl:items-center gap-3">
          <div className="flex flex-1 flex-wrap items-center gap-2">
            <select value={estado} onChange={(e) => setEstado(e.target.value)} className="px-3 py-2 rounded-lg bg-white/70 border border-white/60 text-sm shadow-sm">
              {estadosUnicos.map((op) => (
                <option key={op} value={op}>{op === TODOS ? "Todos los estados" : op[0] + op.slice(1).toLowerCase()}</option>
              ))}
            </select>

            <select value={tipo} onChange={(e) => setTipo(e.target.value)} className="px-3 py-2 rounded-lg bg-white/70 border border-white/60 text-sm shadow-sm">
              {tiposUnicos.map((op) => (
                <option key={op} value={op}>{op === TODOS ? "Todos los tipos" : op.replaceAll("_", " ")}</option>
              ))}
            </select>

            <div className="flex items-center gap-2">
              <input type="date" value={desde} max={hasta || undefined} onChange={(e) => setDesde(e.target.value)}
                     className="px-3 py-2 rounded-lg bg-white/70 border border-white/60 text-sm shadow-sm" aria-label="Fecha desde" />
              <span className="text-slate-400 text-sm" aria-hidden>→</span>
              <input type="date" value={hasta} min={desde || undefined} onChange={(e) => setHasta(e.target.value)}
                     className="px-3 py-2 rounded-lg bg-white/70 border border-white/60 text-sm shadow-sm" aria-label="Fecha hasta" />
            </div>
          </div>

          <ToolbarSearch searchRef={searchRef} q={q} setQ={setQ} onRefrescar={onRefrescar} onExport={handleExport} onCopy={handleCopy} />
        </div>

        <p className="text-xs text-slate-600 mt-2" aria-live="polite">
          Mostrando {ordenadas.length === 0 ? "0" : `${(page - 1) * perPage + 1}–${Math.min(page * perPage, ordenadas.length)}`} de {ordenadas.length} resultado{ordenadas.length === 1 ? "" : "s"}
        </p>
      </div>

      {/* Tabla */}
      <div className="overflow-x-auto rounded-2xl bg-white/55 backdrop-blur-xl border border-white/50 shadow-lg">
        <table className="w-full text-sm">
          <caption className="sr-only">Listado de mis solicitudes</caption>
          <thead className="sticky top-0 z-10 bg-white/70 backdrop-blur-xl">
            <tr className="text-slate-700">
              {COLS.map((c) => (
                <th key={c.key} className="px-4 py-3 text-left font-semibold">
                  {c.key !== "acciones" ? (
                    <button onClick={() => toggleSort(c.key)}
                            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), toggleSort(c.key))}
                            aria-label={`Ordenar por ${c.label}`} aria-sort={ariaSortFor(c.key)}
                            className="inline-flex items-center gap-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-300 rounded px-1 -mx-1">
                      {c.label}
                      <SortGlyph active={sort.key === c.key} dir={sort.dir} />
                    </button>
                  ) : c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {cargando ? (
              <tr><td colSpan={COLS.length} className="px-4 py-8 text-center text-slate-500">Cargando…</td></tr>
            ) : paginated.length === 0 ? (
              <tr><td colSpan={COLS.length} className="px-4 py-10"><EmptyState onClear={() => { setQ(""); setDesde(""); setHasta(""); setEstado(TODOS); setTipo(TODOS); }} /></td></tr>
            ) : (
              paginated.map((a) => {
                const d = getDisplay(a);
                const key = a.id ?? `${toISODate(d.fecha_inicio)}_${toISODate(d.fecha_fin)}_${safe(a.tipo)}_${safe(a.subtipo)}`;
                const enabled = canOperate(a) && !!token;

                const isReq = !!a?.kind && a.kind !== "AUSENCIA" || !!a?._is_request;
                const tipoReal = isReq ? (a.tipo_base || a.tipo) : a.tipo;

                return (
                  <tr key={key} className="border-t border-white/60 hover:bg-white/70 transition">
                    <td className="px-4 py-2 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 rounded-lg ${TIPO_BADGE[tipoReal] || TIPO_BADGE.OTRA}`}>
                          {combineTipoSubtipo(tipoReal, a.subtipo)}
                        </span>
                        {a.kind && (
                          <span className={`px-2 py-0.5 rounded-lg text-xs font-semibold ${kindBadgeClass(a.kind)}`}>
                            {a.kind === "CAMBIO" ? "Solicitud de cambio" : a.kind === "CANCELACION" ? "Solicitud de cancelación" : a.kind}
                          </span>
                        )}
                        {a._is_request && !a.kind && (
                          <span className={`px-2 py-0.5 rounded-lg text-xs font-semibold ${kindBadgeClass(a._request_kind === "change" ? "CAMBIO" : "CANCELACION")}`}>
                            {a._request_kind === "change" ? "Solicitud de cambio" : "Solicitud de cancelación"}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      {renderFechas(d)}
                    </td>
                    <td className="px-4 py-2">{d.parcial ? "Sí" : "No"}</td>
                    <td className="px-4 py-2">{a.retribuida ? "Sí" : "No"}</td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      <span className={`px-2 py-1 rounded-lg text-xs font-semibold ${ESTADO_CLASE[a.estado] || "bg-slate-100 text-slate-700 ring-1 ring-slate-300/50"}`}>
                        {safe(a.estado)}
                      </span>
                    </td>
                    <td className="px-4 py-2">
                      <span className="block max-w-[40ch] truncate text-slate-800" title={safe(a.motivo)}>
                        {safe(a.motivo) || "—"}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right">
                      <div className="inline-flex gap-2">
                        <button
                          disabled={!enabled}
                          onClick={() => openEdit(a)}
                          className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${enabled ? "bg-blue-600 text-white hover:bg-blue-700" : "bg-slate-200 text-slate-500 cursor-not-allowed"}`}
                          title={enabled ? "Solicitar cambio" : "Sólo futuras en estado pendiente/aprobada (no solicitudes)"}
                        >
                          Editar
                        </button>
                        <button
                          disabled={!enabled}
                          onClick={() => openCancel(a)}
                          className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${enabled ? "bg-rose-600 text-white hover:bg-rose-700" : "bg-slate-200 text-slate-500 cursor-not-allowed"}`}
                          title={enabled ? "Solicitar cancelación" : "Sólo futuras en estado pendiente/aprobada (no solicitudes)"}
                        >
                          Cancelar
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <Paginacion
        pagina={page}
        totalPaginas={totalPages}
        totalItems={total}
        porPagina={perPage}
        onAnterior={() => setPage((p) => Math.max(1, p - 1))}
        onSiguiente={() => setPage((p) => Math.min(totalPages, p + 1))}
        onGoToPage={setPage}
        showFirstLast
      />

      {/* (modales iguales a los tuyos) */}
      {editOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center px-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl space-y-4">
            <h3 className="text-lg font-bold text-slate-900">Solicitar cambio</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-slate-600 mb-1">Fecha inicio</label>
                <input type="date" value={editForm.fecha_inicio} onChange={(e)=>setEditForm(f=>({...f,fecha_inicio:e.target.value}))}
                       className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm"/>
              </div>
              <div>
                <label className="block text-xs text-slate-600 mb-1">Fecha fin</label>
                <input type="date" value={editForm.fecha_fin} onChange={(e)=>setEditForm(f=>({...f,fecha_fin:e.target.value}))}
                       className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm"/>
              </div>
              <div className="sm:col-span-2 flex items-center gap-2">
                <input id="parcial" type="checkbox" checked={editForm.parcial}
                       onChange={(e)=>setEditForm(f=>({...f,parcial:e.target.checked}))}/>
                <label htmlFor="parcial" className="text-sm text-slate-700">Parcial</label>
              </div>
              {editForm.parcial && (
                <>
                  <div>
                    <label className="block text-xs text-slate-600 mb-1">Hora inicio</label>
                    <input type="time" value={editForm.hora_inicio} onChange={(e)=>setEditForm(f=>({...f,hora_inicio:e.target.value}))}
                           className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm"/>
                  </div>
                  <div>
                    <label className="block text-xs text-slate-600 mb-1">Hora fin</label>
                    <input type="time" value={editForm.hora_fin} onChange={(e)=>setEditForm(f=>({...f,hora_fin:e.target.value}))}
                           className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm"/>
                  </div>
                </>
              )}
            </div>
            <div className="flex flex-col sm:flex-row justify-end gap-2 pt-1">
              <button onClick={()=>{setEditOpen(false); setEditTarget(null);}}
                      className="px-4 py-2 rounded-xl text-sm font-medium text-gray-700 bg-gray-200 hover:bg-gray-300">
                Cancelar
              </button>
              <button onClick={doSolicitarCambio}
                      className="px-4 py-2 rounded-xl text-sm font-medium text-white bg-blue-600 hover:bg-blue-700">
                Enviar solicitud
              </button>
            </div>
          </div>
        </div>
      )}

      {cancelOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center px-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl space-y-4">
            <h3 className="text-lg font-bold text-slate-900">Solicitar cancelación</h3>
            <p className="text-sm text-slate-600">Indica el motivo de la cancelación:</p>
            <textarea rows={4} value={cancelMotivo} onChange={(e)=>setCancelMotivo(e.target.value)}
                      className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm" placeholder="Motivo…"/>
            <div className="flex flex-col sm:flex-row justify-end gap-2 pt-1">
              <button onClick={()=>{setCancelOpen(false); setCancelTarget(null); setCancelMotivo("");}}
                      className="px-4 py-2 rounded-xl text-sm font-medium text-gray-700 bg-gray-200 hover:bg-gray-300">
                Volver
              </button>
              <button onClick={doSolicitarCancelacion}
                      className="px-4 py-2 rounded-xl text-sm font-medium text-white bg-rose-600 hover:bg-rose-700">
                Enviar solicitud
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ToolbarSearch({ searchRef, q, setQ, onRefrescar, onExport, onCopy }) {
  return (
    <div className="flex items-center gap-2">
      <div className="relative">
        <input
          ref={searchRef}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          type="text"
          placeholder="Buscar motivo, subtipo, fecha…  ( / )"
          className="pl-9 pr-3 py-2 rounded-lg bg-white/70 border border-white/60 text-sm shadow-sm w-64"
          aria-label="Buscar"
        />
        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden>🔎</span>
      </div>
      <button onClick={onRefrescar} className="px-3 py-2 rounded-lg bg-white/70 hover:bg-white border border-white/60 text-sm shadow-sm" title="Refrescar (R)">⟳</button>
      <button onClick={onExport} className="px-3 py-2 rounded-lg bg-white/70 hover:bg-white border border-white/60 text-sm shadow-sm" title="Exportar CSV (E)">⤓ CSV</button>
      <button onClick={onCopy} className="px-3 py-2 rounded-lg bg-white/70 hover:bg-white border border-white/60 text-sm shadow-sm" title="Copiar CSV (C)">⧉ Copiar</button>
    </div>
  );
}

function SortGlyph({ active, dir }) {
  if (!active) return <span className="text-slate-300">↕</span>;
  return <span className="text-slate-600">{dir === "asc" ? "↑" : "↓"}</span>;
}

function EmptyState({ onClear }) {
  return (
    <div className="flex flex-col items-center justify-center text-center py-8">
      <div className="text-4xl mb-2">✨</div>
      <h4 className="text-lg font-semibold text-slate-800">Sin resultados</h4>
      <p className="text-slate-600 text-sm max-w-md">Ajusta los filtros o borra la búsqueda para ver más solicitudes.</p>
      <div className="mt-3 flex items-center gap-2">
        <button onClick={onClear} className="px-3 py-2 rounded-lg bg-white/70 hover:bg-white border border-white/60 text-sm shadow-sm">
          Limpiar filtros
        </button>
      </div>
    </div>
  );
}
